import React from "react";
import { Stack, Typography, Alert } from "@mui/material";
import { Formik, Form, FormikProps } from "formik";
import * as Yup from "yup";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";

import FormAlert from "../components/common/FormAlert";
import FormTextField from "../components/common/FormTextField";
import SubmitButton from "../components/buttons/SubmitButton";

import { useAppDispatch } from "../redux/store";
import { sendFeeReminders } from "../redux/actions/studentFeeLedgerActions";
import { studentFeeLedgerActions } from "../redux/slices/studentFeeLedger";
import { FeeReminderRequestDto } from "../shared/dtos/studentFeeLedger/FeeReminderRequestDto";

const reminderSchema = Yup.object().shape({
  minBalance: Yup.number()
    .required("Threshold balance is required")
    .min(0, "Cannot be negative"),
});

interface SendFeeReminderFormProps {
  classroomId: string;
  classroomName?: string;
  loading: boolean;
  error: string | null;
}

const SendFeeReminderForm: React.FC<SendFeeReminderFormProps> = ({
  classroomId,
  classroomName,
  loading,
  error = null,
}) => {
  const dispatch = useAppDispatch();

  const initialValues: FeeReminderRequestDto = {
    classroomId,
    minBalance: 1000,
  };

  return (
    <Stack spacing={2} sx={{ width: "100%", textAlign: "center" }}>
      <FormAlert
        message={error}
        severity="error"
        closable
        onClose={() =>
          dispatch(studentFeeLedgerActions.setErrorReminders(null))
        }
      />
      <Formik
        initialValues={initialValues}
        validationSchema={reminderSchema}
        onSubmit={(values) => {
          dispatch(
            sendFeeReminders({
              classroomId: values.classroomId,
              minBalance: Number(values.minBalance),
            }),
          );
        }}
      >
        {({ values }: FormikProps<FeeReminderRequestDto>) => (
          <Form>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                You are queueing SMS reminders to parents of students in{" "}
                <b>{classroomName || "the selected classroom"}</b>.
              </Typography>

              <Alert severity="info" sx={{ textAlign: "left" }}>
                Only students with an outstanding balance of{" "}
                <b>KES {Number(values.minBalance || 0).toLocaleString()}</b> or
                more will receive an SMS.
              </Alert>

              <FormTextField
                name="minBalance"
                label="Minimum Balance Threshold (KES)"
                type="number"
                slotProps={{ htmlInput: { min: 0 } }}
              />

              <SubmitButton
                title="Dispatch SMS Reminders"
                loadingText="Queueing..."
                loading={loading}
                disabled={loading}
                startIcon={<NotificationsActiveIcon />}
              />
            </Stack>
          </Form>
        )}
      </Formik>
    </Stack>
  );
};

export default SendFeeReminderForm;
